import {
  PLAIN_STATUS_DOT,
  PLAIN_STATUS_PILL,
  plainStatus,
  plainStatusLabel,
  stepDetail,
  type PlainStatus,
} from '../../lib/plain-status';
import { cn } from '../../lib/utils';
import { statusMoment, useChangeMoment } from './use-moment';

type Props = {
  /** The stream's own state; its group decides the plain label. */
  state?: { name: string; group: string } | null;
  /** Use a plain status directly instead of a state. */
  status?: PlainStatus;
  blocked?: boolean;
  live?: boolean;
  /** Show the stream's own step after the label, e.g. "In progress · QA Passed". */
  showStep?: boolean;
  size?: 'sm' | 'md';
  className?: string;
};

export default function StatusPill({
  state,
  status: given,
  blocked = false,
  live = false,
  showStep = true,
  size = 'sm',
  className,
}: Props) {
  const status = given ?? plainStatus({ group: state?.group, name: state?.name, blocked, live });
  const label = plainStatusLabel(status, { live });
  const step = showStep ? stepDetail(status, state?.name) : null;
  const moment = useChangeMoment(live ? 'live' : status, statusMoment);
  const showCheck = status === 'done' && !live;
  return (
    <span
      className={cn('inline-flex max-w-full items-center gap-1.5 whitespace-nowrap', className)}
      title={step ? `${label} · ${step}` : label}
    >
      <span
        className={cn(
          'inline-flex shrink-0 items-center gap-1.5 rounded-full font-medium transition-colors duration-300',
          moment === 'live' && 'live-flash',
          moment === 'done' && 'animate-done-pop motion-reduce:animate-none',
          moment === 'unblocked' && 'animate-release-pop motion-reduce:animate-none',
          size === 'md' ? 'px-2.5 py-1 text-xs' : 'px-2 py-0.5 text-[11px] leading-4',
          PLAIN_STATUS_PILL[status],
        )}
      >
        {showCheck ? (
          <DoneCheck draw={moment === 'done'} />
        ) : (
          <span
            className={cn('h-1.5 w-1.5 shrink-0 rounded-full', PLAIN_STATUS_DOT[status], moment === 'live' && 'animate-live-ring')}
            aria-hidden
          />
        )}
        {label}
      </span>
      {step ? <span className="min-w-0 truncate text-[11px] text-muted-foreground">{step}</span> : null}
    </span>
  );
}

function DoneCheck({ draw }: { draw: boolean }) {
  return (
    <svg viewBox="0 0 10 10" className="h-2.5 w-2.5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden>
      <polyline
        points="1.5,5.5 4,8 8.5,2.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="14"
        className={cn(draw && 'animate-check-draw motion-reduce:animate-none')}
      />
    </svg>
  );
}
